import { Button, ExternalLinkIcon as ExternalLink, Flex, Label, Link, Select } from '@lokalise/louis';
import { useState } from 'react';

import { useAddUserGroupsMutation } from '@app/api/contributors';
import { showUnexpectedErrorToast } from '@app/components/Toast';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import { ModalFooter, UpgradePlanMessage } from '../styles';

type Group = { value: number; label: string };

export type UserGroupsProps = {
    isGroupsEnabled: boolean | undefined;
    projectId: string;
    groups: Group[];
    onClose: () => void;
};

const UserGroups = ({ isGroupsEnabled, projectId, groups, onClose }: UserGroupsProps) => {
    const [loading, setLoading] = useState(false);
    const [addUserGroups] = useAddUserGroupsMutation();
    const [selectedUserGroups, setSelectedUserGroups] = useState<readonly Group[]>([]);

    const onSubmit = async () => {
        setLoading(true);
        const userGroupIds = selectedUserGroups.map((group) => Number(group.value));
        try {
            await addUserGroups({ projectId, userGroupIds }).unwrap();
            track(AnalyticsEventName.ADD_USER_GROUP_TO_PROJECT, {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                user_group_quantity: userGroupIds.length,
            });
            window.location.reload();
        } catch {
            setLoading(false);
            showUnexpectedErrorToast();
        }
    };

    if (isGroupsEnabled === false) {
        return (
            <>
                <UpgradePlanMessage align="center" direction="column" gap={2}>
                    <span>
                        Get access to user groups when you upgrade to the <strong>Pro</strong> plan.
                    </span>
                    <Link target="_new" href="/team#billing">
                        Upgrade now
                    </Link>
                </UpgradePlanMessage>
                <ModalFooter justify="space-between" align="center">
                    <Link
                        href="https://docs.lokalise.com/translation-workflow/user-groups"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Flex align="center" gap={1}>
                            Learn more about user groups. <ExternalLink />
                        </Flex>
                    </Link>
                </ModalFooter>
            </>
        );
    }

    return (
        <>
            <Label id="userGroups" text="User groups">
                <Select
                    value={selectedUserGroups}
                    options={groups}
                    isMulti
                    onChange={setSelectedUserGroups}
                    aria-labelledby="userGroups"
                    placeholder="Select user groups"
                    menuPosition="fixed"
                />
            </Label>
            <ModalFooter justify="space-between" align="center">
                <Link
                    href="https://docs.lokalise.com/translation-workflow/user-groups"
                    target="_blank"
                    rel="noreferrer"
                >
                    <Flex align="center" gap={1}>
                        Learn more about user groups. <ExternalLink />
                    </Flex>
                </Link>
                <Flex justify="flex-end" gap={2} grow={1}>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onSubmit}
                        disabled={selectedUserGroups.length === 0}
                        loading={loading}
                    >
                        Add user groups
                    </Button>
                </Flex>
            </ModalFooter>
        </>
    );
};

export default UserGroups;
